import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TabViewModule } from 'primeng/tabview';
import { MessagesModule } from 'primeng/messages';
import { Customer } from 'src/app/demo/models/customer';
import { CustomersService } from 'src/app/demo/service/customers.service';
import { ProgressLoaderService } from 'src/app/demo/service/progress-loader.service';
import { TableModule } from 'primeng/table';
import { KycVehiclesListComponent } from '../../common/kyc-vehicles-list/kyc-vehicles-list.component';
import { KycDriversListComponent } from '../../common/kyc-drivers-list/kyc-drivers-list.component';
import {
    DriverProfile,
    DriverSummary,
    KYCDocument,
    Vehicle,
} from 'src/app/demo/models/common-data-types';
import { FormatCapitalizedConstantsService } from 'src/app/demo/service/format-capitalized-constants.service';
import { KycPoliciesListComponent } from '../../common/kyc-policies-list/kyc-policies-list.component';
import { DocumentsService } from 'src/app/demo/service/documents.service';
import { KycCustomerDocumentsListComponent } from '../../common/kyc-customer-documents-list/kyc-customer-documents-list.component';
import { CustomerKYCDocument } from 'src/app/demo/models/documents';
import { ProgressBarModule } from 'primeng/progressbar';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
    DynamicDialogRef,
    DynamicDialogModule,
    DialogService,
} from 'primeng/dynamicdialog';
import { DocumentEntities } from 'src/app/demo/constants/document-entities';
import { VerifyCustomerKycDocumentComponent } from '../../common/verify-customer-kyc-document/verify-customer-kyc-document.component';
import { TooltipModule } from 'primeng/tooltip';
import { NgxTranslateModule } from 'src/app/translate.module';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'src/app/demo/service/language.service';
import { languages } from 'src/app/demo/constants/languages';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { CustomerProfileDocumentViewComponent } from '../../common/customer-profile-document-view/customer-profile-document-view.component';
import { showErrorToast } from 'src/app/demo/utilities/toast-utils';

@Component({
    selector: 'app-customer-detail',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        CardModule,
        NgxTranslateModule,
        MessagesModule,
        ButtonModule,
        TabViewModule,
        TooltipModule,
        TableModule,
        KycVehiclesListComponent,
        KycDriversListComponent,
        KycPoliciesListComponent,
        ProgressBarModule,
    ],
    templateUrl: './customer-detail.component.html',
    styleUrl: './customer-detail.component.scss',
})
export class CustomerDetailComponent {
    customerId!: string;
    customer!: Customer;
    customerDocuments!: CustomerKYCDocument[];
    loadingDocumentView: boolean = false;

    blobUrl: SafeResourceUrl | null = null;
    ref: DynamicDialogRef | undefined;
    mimeType!: any;

    drivers!: DriverSummary[];
    vehicles!: Vehicle[];

    languages: any[] = languages;
    selectedLanguage!: string;

    constructor(
        private route: ActivatedRoute,
        private customerService: CustomersService,
        private router: Router,
        private translateService: TranslateService,
        private languageService: LanguageService,
        private dialogService: DialogService,
        private progressLoaderService: ProgressLoaderService,
        private formatService: FormatCapitalizedConstantsService,
        private documentsService: DocumentsService,
        private messageService:MessageService,
        private sanitizer: DomSanitizer
    ) {}

    ngOnInit() {
        this.route.params.subscribe((params) => {
            this.customerId = params['id'];
            this.fetchCustomerDetail();
            this.fetchCustomerDrivers();
            this.fetchCustomerVehicles();
            this.fetchCustomerDocuments();
        });
        this.initializeLocale();
    }

    initializeLocale() {
        this.languageService.currentLanguage$.subscribe((language) => {
            this.selectedLanguage = language;
            this.setLanguage(this.selectedLanguage);
        });
    }

    setLanguage(language: string) {
        this.translateService.use(language);
        this.languageService.setLanguage(language);
    }

    languageChanged(event: DropdownChangeEvent) {
        this.setLanguage(event.value);
    }

    fetchCustomerDocuments() {
        this.documentsService
            .fetchCustomerDocuments(this.customerId)
            .subscribe({
                next: (value) => {
                    this.customerDocuments = value;
                },
                error: (err) => {
                    showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
                },
            });
    }

    formatText(value: string | undefined): string {
        return value === undefined
            ? '---'
            : this.formatService.formatConstantToNormalText(value);
    }

    fetchCustomerDetail() {
        this.progressLoaderService.show();
        this.customerService.fetchCustomerDetail(this.customerId).subscribe({
            next: (value) => {
                this.customer = value;
                this.progressLoaderService.hide();
            },
            error: (err) => {
                this.progressLoaderService.hide();
                showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService, this.translateService)
            },
        });
    }

    closeClicked() {
        this.router.navigate(['/home/customers/search']);
    }

    fetchCustomerVehicles() {
        this.customerService.fetchCustomerVehicles(this.customerId).subscribe({
            next: (value) => {
                this.vehicles = value;
            },
            error: (err) => {
                showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService, this.translateService)
            },
        });
    }

    fetchCustomerDrivers() {
        this.customerService.fetchCustomerDrivers(this.customerId).subscribe({
            next: (value) => {
                this.drivers = value;
            },
            error: (err) => {
                showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService, this.translateService)
            },
        });
    }

    getKycDocument(id: string) {
        this.loadingDocumentView = true;
        this.documentsService.getDocumentById(id).subscribe((res) => {
            this.loadingDocumentView = false;
            const blobUrl = URL.createObjectURL(this.getBlobItem(res));
            this.blobUrl =
                this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
            this.viewCustomerDocument(res.body.type, id);
        });
    }

    getBlobItem(res: any): Blob {
        if (res.body.type === 'application/pdf') {
            return new Blob([res.body], {
                type: 'application/pdf',
            });
        } else if (res.body.type === 'image/png') {
            return new Blob([res.body], { type: 'image/png' });
        } else if (res.body.type === 'image/jpeg') {
            return new Blob([res.body], { type: 'image/jpeg' });
        } else {
            return new Blob([res.body], {
                type: 'application/octet-stream',
            });
        }
    }

    viewCustomerDocument(mimeType: string, documentId: string) {
        const verificationData = {
            blobUrl: this.blobUrl,
            mimeType: mimeType,
            documentId: documentId,
            entityId: this.customerId,
            entity: DocumentEntities.CUSTOMER,
            customerId: this.customerId,
        };
        this.ref = this.dialogService.open(VerifyCustomerKycDocumentComponent, {
            width: '85%',
            showHeader: false,
            contentStyle: { overflow: 'scroll' },
            baseZIndex: 10000,
            maximizable: false,
            data: verificationData,
        });

        this.ref.onClose.subscribe((result) => {
            this.fetchCustomerDocuments();
        });
    }

    openVerifiedCustomerDocument(documentId: string, mimeType: string) {
        this.loadingDocumentView=true;
        this.documentsService.getDocumentById(documentId).subscribe((res) => {
            this.loadingDocumentView = false;
            const blobUrl = URL.createObjectURL(this.getBlobItem(res));
            this.blobUrl =
                this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);

            const documentDetailData = {
                documentId: documentId,
                blobUrl: this.blobUrl,
                mimeType: mimeType,
                title: this.translateService.instant("Profile Document")
            };

            this.ref = this.dialogService.open(
                CustomerProfileDocumentViewComponent,
                {
                    width: '85%',
                    showHeader: false,
                    contentStyle: { overflow: 'scroll' },
                    baseZIndex: 10000,
                    maximizable: false,
                    data: documentDetailData,
                }
            );
        });
    }
}
